// MACHINERY V2 (Stroje) — the phone nav drawer's row atoms (M2.16a).
//
// Split from MobileDrawer.tsx so that file stays composition + gating and this
// one is presentation. Nothing here knows what a module is or who may see it.
//
// >>> ONLY WHAT THE KIT DOES NOT ALREADY HAVE. <<< The drawer is a SheetV2, so
// its sections and its chips are the sheet kit's SheetSection / SheetChip
// (mobile/sheetAtoms.tsx) — this file shipped a `DrawerSection` byte-identical
// to SheetSection and a `DrawerLangChip` that was SheetChip with a mono
// typeface, which is a variant, not a component. Both are gone; the kit's
// atoms are used directly and SheetChip gained `variant="code"` for the
// two-letter language and palette tokens. What is LEFT here is the two shapes
// the kit genuinely does not have: a nav row with an unread badge, and a
// non-interactive identity block.
//
// EVERY ROW IS >= 48px, and the identity block is not a target at all — the
// law is ">= 44px BOTH axes below 1280", and a nav list is the one place a
// mis-tap costs a whole page load. The rows are `width: 100%`, so the width
// half of the law is satisfied by the layout rather than by a minWidth.
//
// No Tailwind, no shadcn, no MUI, no @emotion, no sx, no dt tokens.
import React from "react";

export interface DrawerRowProps {
  label: string;
  icon?: React.ReactNode;
  /** Tints the icon and the label — the active module's accent, or the BRAND
   *  accent for Midas. */
  accent?: string;
  active?: boolean;
  /** Right-aligned count (the bell's unread badge). */
  badge?: number;
  onSelect: () => void;
}

export const DrawerRow: React.FC<DrawerRowProps> = ({
  label,
  icon,
  accent,
  active = false,
  badge,
  onSelect,
}) => (
  <button
    type="button"
    onClick={onSelect}
    aria-current={active ? "page" : undefined}
    style={{
      display: "flex",
      alignItems: "center",
      gap: 12,
      width: "100%",
      minHeight: 48,
      padding: "0 4px",
      border: 0,
      borderRadius: 8,
      background: active ? "var(--mod-accent-soft)" : "transparent",
      cursor: "pointer",
      fontFamily: "inherit",
      fontSize: 14,
      fontWeight: active ? 600 : 450,
      color: active || accent ? accent || "var(--mod-accent)" : "var(--ink-2)",
      textAlign: "left",
      // 44, NEVER 0 — the pair to `minHeight` above, and the same one-token fix
      // TabSelect and SpecSectionsV2 took. Inert today (`width: "100%"` inside
      // the sheet body), but a `0` beside a stated minimum is what silently
      // voids a floor. Truncation is the label span's job, not this one's.
      minWidth: 44,
    }}
  >
    {icon && (
      <span style={{ display: "inline-flex", flexShrink: 0, width: 18, justifyContent: "center" }}>
        {icon}
      </span>
    )}
    <span
      style={{
        flex: 1,
        minWidth: 0,
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
      }}
    >
      {label}
    </span>
    {badge !== undefined && badge > 0 && <DrawerBadge count={badge} />}
  </button>
);

/** The unread pill. Exported because the phone HEADER prints the same number on
 *  the hamburger — a badge the drawer can only show once it is already open is
 *  not a reason to open it. Same shape, same clamp, one drawing. */
export const DrawerBadge: React.FC<{ count: number }> = ({ count }) => (
  <span
    aria-hidden
    style={{
      minWidth: 18,
      height: 18,
      padding: "0 5px",
      borderRadius: 999,
      background: "var(--red)",
      color: "#fff",
      fontFamily: "var(--font-mono)",
      fontSize: 10,
      fontWeight: 700,
      display: "grid",
      placeItems: "center",
      flexShrink: 0,
    }}
  >
    {count > 99 ? "99+" : count}
  </span>
);

/** Identity block — avatar, name, role · company. Not interactive: it is data
 *  the user already owns, and the actions that act on it are rows below. */
export const DrawerIdentity: React.FC<{
  initial: string;
  name: string;
  subLine: string;
  accent: string;
}> = ({ initial, name, subLine, accent }) => (
  <div style={{ display: "flex", alignItems: "center", gap: 11, padding: "4px 0 10px" }}>
    <span
      style={{
        width: 34,
        height: 34,
        borderRadius: 999,
        background: accent,
        color: "#fff",
        display: "grid",
        placeItems: "center",
        fontSize: 13,
        fontWeight: 600,
        flexShrink: 0,
      }}
    >
      {initial}
    </span>
    <div style={{ minWidth: 0 }}>
      <div
        style={{
          fontSize: 14,
          fontWeight: 600,
          color: "var(--ink)",
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {name}
      </div>
      {subLine && (
        <div
          style={{
            fontFamily: "var(--font-mono)",
            fontSize: 9.5,
            color: "var(--faint)",
            letterSpacing: "0.04em",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {subLine}
        </div>
      )}
    </div>
  </div>
);
